import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupsIcon from '@mui/icons-material/Groups';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import VerifiedIcon from '@mui/icons-material/Verified';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const About = () => {
  const theme = useTheme();

  const stats = [
    { number: '500K+', label: 'Orders Delivered', icon: <VerifiedIcon sx={{ fontSize: 40 }} /> },
    { number: '10K+', label: 'Happy Customers', icon: <GroupsIcon sx={{ fontSize: 40 }} /> },
    { number: '26', label: 'States Covered', icon: <PublicIcon sx={{ fontSize: 40 }} /> },
    { number: '10K+', label: 'Products', icon: <TrendingUpIcon sx={{ fontSize: 40 }} /> },
  ];

  const values = [
    {
      icon: <VerifiedIcon sx={{ fontSize: 50, color: '#006efa' }} />,
      title: 'Quality First',
      description: 'We never compromise on quality. All our materials are tested and certified to meet industry standards.',
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 50, color: '#006efa' }} />,
      title: 'Customer Centric',
      description: 'Our customers are at the heart of everything we do. We strive to exceed expectations at every touchpoint.',
    },
    {
      icon: <FavoriteIcon sx={{ fontSize: 50, color: '#006efa' }} />,
      title: 'Integrity',
      description: 'We believe in transparent and ethical business practices, building trust with every transaction.',
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 50, color: '#006efa' }} />,
      title: 'Innovation',
      description: 'Constantly evolving our platform and services to provide cutting-edge solutions for modern businesses.',
    },
  ];

  const milestones = [
    { year: '2015', event: 'Loha Mart Founded', description: 'Started with a vision to digitize B2B raw materials procurement' },
    { year: '2017', event: 'Pan-India Expansion', description: 'Expanded operations to cover 26 states across India' },
    { year: '2019', event: 'Credit Facility Launch', description: 'Introduced flexible credit options for SMEs' },
    { year: '2021', event: '500K Orders Milestone', description: 'Served over 500,000 orders to satisfied customers' },
    { year: '2023', event: 'Digital Transformation', description: 'Launched AI-powered procurement and pricing intelligence' },
    { year: '2024', event: 'Industry Leader', description: 'Recognized as India\'s leading B2B materials platform' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #006efa 0%, #07cdbe 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontSize: { xs: '2rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            About Loha Mart
          </Typography>
          <Typography
            variant="h5"
            align="center"
            sx={{
              fontSize: { xs: '1rem', md: '1.5rem' },
              opacity: 0.95,
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Transforming B2B raw materials procurement through technology, trust, and transparency
          </Typography>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: -4, mb: 8 }}>
        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    mb: 2,
                  }}
                >
                  {stat.icon}
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {stat.number}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom fontWeight={700}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Founded in 2015, Loha Mart began with a simple yet powerful vision: to revolutionize the way
              businesses procure raw materials in India. We recognized that SMEs and manufacturers faced
              significant challenges in sourcing quality materials at competitive prices.
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Starting from a small office in Mumbai, we've grown into India's largest B2B raw materials
              procurement platform, serving over 10,000 businesses across 26 states. Our journey has been
              driven by innovation, customer trust, and an unwavering commitment to quality.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              Today, we're not just a marketplace we're a complete ecosystem providing procurement,
              pricing intelligence, credit facilities, and logistics support to empower businesses
              to grow and succeed.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600"
              alt="Our Story"
              sx={{
                width: '100%',
                borderRadius: 3,
                boxShadow: 4,
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Vision & Mission Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <VisibilityIcon sx={{ fontSize: 50, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h4" fontWeight={700}>
                    Our Vision
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  To become the most trusted and innovative B2B materials procurement platform in India,
                  empowering every business with access to quality materials, competitive pricing, and
                  flexible credit options that fuel growth and success.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'secondary.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <TrackChangesIcon sx={{ fontSize: 50, color: 'secondary.main', mr: 2 }} />
                  <Typography variant="h4" fontWeight={700}>
                    Our Mission
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  To simplify industrial procurement through technology, provide transparent pricing,
                  ensure quality assurance, and deliver exceptional customer service. We aim to be
                  the preferred partner for businesses seeking reliable and efficient material sourcing solutions.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Values Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          fontWeight={700}
          sx={{ mb: 1 }}
        >
          Our Core Values
        </Typography>
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, fontSize: '1.1rem' }}
        >
          The principles that guide everything we do
        </Typography>

        <Grid container spacing={4}>
          {values.map((value, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  {value.icon}
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {value.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {value.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Timeline Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            fontWeight={700}
            sx={{ mb: 1 }}
          >
            Our Journey
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, fontSize: '1.1rem' }}
          >
            Key milestones in our growth story
          </Typography>

          <Grid container spacing={3}>
            {milestones.map((milestone, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    display: 'flex',
                    p: 3,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'translateX(8px)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      backgroundColor: 'primary.main',
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      mr: 3,
                    }}
                  >
                    {milestone.year}
                  </Avatar>
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {milestone.event}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {milestone.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #006efa 0%, #07cdbe 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" gutterBottom fontWeight={700}>
              Join Our Growing Family
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Become part of India's largest B2B materials procurement community
            </Typography>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default About;
